import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, FileText } from "lucide-react";

import Layout from "../components/Layout";
import { API } from "../config/api";

import AskReportBox from "../components/reports/AskReportBox";
import ReportTemplateCard from "../components/reports/ReportTemplateCard";
import ReportPreview from "../components/reports/ReportPreview";
import ReportKpiCards from "../components/reports/ReportKpiCards";
import ReportTableBlock from "../components/reports/ReportTableBlock";
import ReportChartBlock from "../components/reports/ReportChartBlock";
import ReportInsightPanel from "../components/reports/ReportInsightPanel";
import ReportExportButtons from "../components/reports/ReportExportButtons";
import RecentReportsList from "../components/reports/RecentReportsList";
import SavedReportsList from "../components/reports/SavedReportsList";
import ReportFilterDialog from "../components/reports/ReportFilterDialog";
import SmartQueryConfirm from "../components/reports/SmartQueryConfirm";

const ReportsPage = () => {
  const [templates, setTemplates] = useState([]);
  const [recents, setRecents] = useState([]);
  const [savedViews, setSavedViews] = useState([]);
  const [askLoading, setAskLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [pendingFilters, setPendingFilters] = useState({});
  const [filterOpen, setFilterOpen] = useState(false);
  const [report, setReport] = useState(null);
  const [smartQuery, setSmartQuery] = useState(null);
  const [smartRunning, setSmartRunning] = useState(false);

  const loadInitial = useCallback(async () => {
    try {
      const [tRes, hRes, sRes] = await Promise.all([
        axios.get(`${API}/reports/templates`),
        axios.get(`${API}/reports/history?limit=10`),
        axios.get(`${API}/reports/saved-views/list`),
      ]);
      setTemplates(tRes.data?.templates || []);
      setRecents(hRes.data?.reports || []);
      setSavedViews(sRes.data?.views || []);
    } catch (e) {
      toast.error("Failed to load Report Studio");
    }
  }, []);

  useEffect(() => { loadInitial(); }, [loadInitial]);

  const openTemplate = (template, prefilledFilters = {}) => {
    if (!template.accessible) {
      toast.error("This report is not available for your role");
      return;
    }
    if (template.status !== "ready") {
      toast.info("This template is coming soon");
      return;
    }
    setActiveTemplate(template);
    setPendingFilters(prefilledFilters);
    setFilterOpen(true);
  };

  const runReport = async (filters) => {
    if (!activeTemplate) return;
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/reports/generate`, {
        template_id: activeTemplate.template_id,
        filters,
      });
      setReport(r.data);
      setFilterOpen(false);
      // Refresh recents
      const h = await axios.get(`${API}/reports/history?limit=10`);
      setRecents(h.data?.reports || []);
      // Smooth scroll to the preview
      setTimeout(() => {
        document.getElementById("report-preview-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
      toast.success(`${activeTemplate.name} generated`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Generation failed";
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  const openExistingReport = async (report_id) => {
    try {
      const r = await axios.get(`${API}/reports/${report_id}`);
      setReport(r.data);
      setTimeout(() => {
        document.getElementById("report-preview-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
    } catch {
      toast.error("Could not load report");
    }
  };

  const handleAsk = async (question) => {
    setAskLoading(true);
    setSmartQuery(null);
    try {
      const r = await axios.post(`${API}/reports/ask`, { question });
      const { suggestion, filters, confidence, explanation, smart_query } = r.data;

      // 1) High-confidence template match → straight to filter dialog
      if (suggestion && confidence === "high" && suggestion.accessible) {
        const template = templates.find((t) => t.template_id === suggestion.template_id);
        if (template) {
          setActiveTemplate(template);
          setPendingFilters(filters || {});
          setFilterOpen(true);
          toast.message(explanation || "Confirm filters and generate");
          return;
        }
      }

      // 2) Otherwise prefer Smart Query if available (medium/low template confidence
      //    often means the LLM grabbed the wrong template for a hygiene question).
      if (smart_query) {
        setSmartQuery({ ...smart_query, question });
        toast.message(explanation || "I built a custom query — review and run it");
        setTimeout(() => {
          document.getElementById("smart-query-anchor")?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 80);
        return;
      }

      // 3) No smart query but we have a (lower-confidence) template match
      if (suggestion) {
        if (!suggestion.accessible) {
          toast.error(suggestion.blocked_reason || "You can't run that report");
          return;
        }
        const template = templates.find((t) => t.template_id === suggestion.template_id);
        if (template) {
          openTemplate(template, filters);
          toast.message(explanation || "Please confirm the filters before generating");
          return;
        }
      }

      // 4) Truly unmappable
      toast.info(explanation || "I couldn't map your question. Try a template below.", { duration: 5000 });
    } catch {
      toast.error("Ask AI failed");
    } finally {
      setAskLoading(false);
    }
  };

  const runSmartQuery = async () => {
    if (!smartQuery) return;
    setSmartRunning(true);
    try {
      const r = await axios.post(`${API}/reports/ask-smart-run`, {
        question: smartQuery.question,
        spec: smartQuery.spec,
      });
      setReport(r.data);
      setSmartQuery(null);
      const h = await axios.get(`${API}/reports/history?limit=10`);
      setRecents(h.data?.reports || []);
      setTimeout(() => {
        document.getElementById("report-preview-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
      toast.success("Smart query complete");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Smart query failed";
      toast.error(msg);
    } finally {
      setSmartRunning(false);
    }
  };

  const refreshSaved = async () => {
    try {
      const r = await axios.get(`${API}/reports/saved-views/list`);
      setSavedViews(r.data?.views || []);
    } catch {}
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <header data-testid="reports-page-header" className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-wider font-semibold text-violet-700">
              <Sparkles className="w-3.5 h-3.5" /> AI Report Studio
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight mt-1">
              Generate decision-ready CRM reports from live sales data.
            </h1>
            <p className="text-sm text-slate-500 mt-1 max-w-2xl">
              Every number comes from your CRM. The AI only explains trends and suggests actions — never invents data.
            </p>
          </div>
        </header>

        <AskReportBox onAsk={handleAsk} loading={askLoading} />

        {smartQuery && (
          <div id="smart-query-anchor">
            <SmartQueryConfirm
              smartQuery={smartQuery}
              question={smartQuery.question}
              onRun={runSmartQuery}
              onCancel={() => setSmartQuery(null)}
              running={smartRunning}
            />
          </div>
        )}

        <section data-testid="report-templates-section" className="space-y-3">
          <div className="flex items-baseline justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-700">Report Templates</h2>
            <span className="text-xs text-slate-400">{templates.filter(t => t.status === "ready").length} ready · {templates.filter(t => t.status === "coming_soon").length} coming soon</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {templates.map((t) => (
              <ReportTemplateCard key={t.template_id} template={t} onSelect={(tpl) => openTemplate(tpl)} />
            ))}
          </div>
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <section data-testid="saved-section" className="space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-700">Saved Reports</h2>
            <SavedReportsList views={savedViews} onOpen={openExistingReport} onChanged={refreshSaved} />
          </section>
          <section data-testid="recent-section" className="space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-700">Recent Reports</h2>
            <RecentReportsList reports={recents} onOpen={openExistingReport} />
          </section>
        </div>

        <div id="report-preview-anchor" />

        {report ? (
          <section data-testid="report-output-section" className="space-y-4 pt-2">
            <ReportPreview report={report}>
              <ReportExportButtons report={report} onSaved={refreshSaved} />
            </ReportPreview>

            <ReportInsightPanel
              summary={report.ai_summary}
              insights={report.ai_insights}
              recommendations={report.recommendations}
            />

            <ReportKpiCards kpis={report.kpis} />

            {report.charts?.length > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {report.charts.map((c, i) => (
                  <ReportChartBlock key={i} chart={c} />
                ))}
              </div>
            )}

            <div className="space-y-4">
              {(report.tables || []).map((t, i) => (
                <ReportTableBlock key={i} table={t} />
              ))}
            </div>
          </section>
        ) : (
          <section data-testid="reports-empty-state" className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 p-10 text-center">
            <FileText className="w-8 h-8 text-slate-300 mx-auto" />
            <p className="mt-3 text-sm text-slate-600 font-medium">
              Choose a report template or ask a question to generate your first report.
            </p>
            <p className="mt-1 text-xs text-slate-400">
              We pull live numbers from your CRM. The AI explains what they mean.
            </p>
          </section>
        )}
      </div>

      <ReportFilterDialog
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        template={activeTemplate}
        initialFilters={pendingFilters}
        onGenerate={runReport}
        busy={generating}
      />
    </Layout>
  );
};

export default ReportsPage;
